(() => {
  'use strict';

  if (!customElements.get('wi-reel-shop')) {
    customElements.define('wi-reel-shop', class WiReelShop extends HTMLElement {
      connectedCallback() {
        if (this.dataset.wirsInitialized === 'true') return;
        this.dataset.wirsInitialized = 'true';

        const root = this;
        const routeRoot = () => {
          const path = window.Shopify?.routes?.root || '/';
          return path.endsWith('/') ? path : `${path}/`;
        };

        const cloneTemplate = (template) => {
          if (!template?.content) return null;
          return template.content.cloneNode(true);
        };

        const lightbox = root.querySelector('[data-wirs-lightbox]');
        const viewer = root.querySelector('[data-wirs-viewer]');
        const lightboxVideo = root.querySelector('[data-wirs-lightbox-video]');
        const productSlot = root.querySelector('[data-wirs-product-slot]');
        const playButton = root.querySelector('[data-wirs-play]');
        const muteButton = root.querySelector('[data-wirs-mute]');
        const closeButton = root.querySelector('[data-wirs-close]');
        const prevButton = root.querySelector('[data-wirs-prev]');
        const nextButton = root.querySelector('[data-wirs-next]');
        const counterCurrent = root.querySelector('[data-wirs-counter-current]');
        const variantSheet = root.querySelector('[data-wirs-variant-sheet]');
        const variantSlot = root.querySelector('[data-wirs-variant-slot]');
        const variantBackdrop = root.querySelector('[data-wirs-variant-backdrop]');
        const toast = root.querySelector('[data-wirs-toast]');
        const toastName = root.querySelector('[data-wirs-toast-name]');

        if (!lightbox || !viewer || !lightboxVideo || !productSlot || !playButton || !muteButton || !closeButton || !variantSheet || !variantSlot || !variantBackdrop) {
          root.dataset.wirsInitialized = 'false';
          return;
        }

        const cards = Array.from(root.querySelectorAll('[data-wirs-card]'));
        const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
        const baseRoute = routeRoot();
        const cartAddUrl = `${baseRoute}cart/add.js`;

        let currentIndex = 0;
        let lastFocus = null;
        let previousHtmlOverflow = '';
        let previousBodyOverflow = '';
        let variantController = null;
        let toastTimer = 0;
        let isAdding = false;
        let pointerStartX = 0;
        let pointerStartY = 0;
        let destroyed = false;
        let userUnmutedAudio = false;
        let stallTimer = 0;
        let stallRetried = false;
        const variantCache = new Map();

        const previewRatios = new Map();
        const previewPlaying = new Set();
        const previewTimers = new Map();
        const PREVIEW_VISIBLE_RATIO = 0.2;
        const PREVIEW_STAGGER_MS = 60;
        let previewObserver = null;
        let previewSyncQueued = false;
        let previewStarted = false;
        let previewFailures = 0;
        let previewDisabled = false;

        const getCard = (index) => cards[index] || null;
        const getWrappedIndex = (index) => {
          if (!cards.length) return 0;
          return (index + cards.length) % cards.length;
        };

        const getTemplate = (type, index) =>
          root.querySelector(`[data-wirs-${type}-template="${index}"]`);

        const previewConnection = () =>
          navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

        const previewsAllowed = () => {
          if (previewDisabled || reducedMotionQuery?.matches) return false;

          const connection = previewConnection();
          if (connection?.saveData) return false;
          if (['slow-2g', '2g', '3g'].includes(connection?.effectiveType || '')) return false;
          if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory > 0 && navigator.deviceMemory < 2) return false;

          return true;
        };

        const attachPreview = (card) => {
          if (!card || card.__wirsPreview) return card?.__wirsPreview || null;

          const src = card.dataset.preview || card.dataset.video || '';
          const media = card.querySelector('.wi-reel-shop__media');
          if (!src || !media) return null;

          const video = document.createElement('video');
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.preload = 'none';
          video.disablePictureInPicture = true;
          video.disableRemotePlayback = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('disableremoteplayback', '');
          video.setAttribute('aria-hidden', 'true');
          video.setAttribute('tabindex', '-1');
          video.dataset.wirsPreview = '';
          video.dataset.src = src;
          video.width = 405;
          video.height = 720;

          media.appendChild(video);
          card.__wirsPreview = video;
          return video;
        };

        const startPreview = (card) => {
          previewTimers.delete(card);
          if (destroyed || !previewPlaying.has(card)) return;

          const video = attachPreview(card);
          if (!video) {
            previewPlaying.delete(card);
            return;
          }

          if (!video.getAttribute('src')) {
            video.setAttribute('src', video.dataset.src || '');
            video.load();
          }

          video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });

          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(() => {
              previewPlaying.delete(card);
              video.classList.remove('is-playing');
              previewFailures += 1;
              if (previewFailures >= 3) {
                previewDisabled = true;
                teardownPreviews();
              }
            });
          }
        };

        const queuePreview = (card, order) => {
          if (!card || previewPlaying.has(card)) return;
          previewPlaying.add(card);

          if (order <= 0) {
            startPreview(card);
            return;
          }

          previewTimers.set(card, window.setTimeout(() => startPreview(card), order * PREVIEW_STAGGER_MS));
        };

        const stopPreview = (card, release = false) => {
          if (!card) return;
          previewPlaying.delete(card);

          const timer = previewTimers.get(card);
          if (timer) {
            window.clearTimeout(timer);
            previewTimers.delete(card);
          }

          const video = card.__wirsPreview;
          if (!video) return;

          video.classList.remove('is-playing');
          if (!video.paused) video.pause();

          if (release && video.getAttribute('src')) {
            video.removeAttribute('src');
            video.load();
          }
        };

        const pauseAllPreviews = (release = false) => {
          cards.forEach((card) => stopPreview(card, release));
        };

        const syncPreviews = () => {
          previewSyncQueued = false;
          if (destroyed) return;

          if (!previewsAllowed() || document.hidden || lightbox.classList.contains('is-open')) {
            pauseAllPreviews(false);
            return;
          }

          let order = 0;
          cards.forEach((card) => {
            const ratio = previewRatios.get(card) || 0;
            if (ratio >= PREVIEW_VISIBLE_RATIO) {
              if (!previewPlaying.has(card)) {
                queuePreview(card, order);
                order += 1;
              }
            } else {
              stopPreview(card, false);
            }
          });
        };

        const queuePreviewSync = () => {
          if (previewSyncQueued || destroyed) return;
          previewSyncQueued = true;
          window.requestAnimationFrame(syncPreviews);
        };

        const setupPreviewObserver = () => {
          if (previewStarted || destroyed || !previewsAllowed() || !cards.length) return;
          if (typeof window.IntersectionObserver !== 'function') return;

          previewStarted = true;
          previewObserver = new window.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              previewRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
              if (entry.isIntersecting) warmProductImage(entry.target);
            });
            queuePreviewSync();
          }, {
            root: null,
            rootMargin: '80px 0px',
            threshold: [0, 0.2, 0.4, 0.6, 0.8, 1]
          });

          cards.forEach((card) => previewObserver.observe(card));
        };

        const teardownPreviews = () => {
          previewObserver?.disconnect();
          previewObserver = null;
          previewStarted = false;
          previewTimers.forEach((timer) => window.clearTimeout(timer));
          previewTimers.clear();
          pauseAllPreviews(true);
          previewRatios.clear();
        };

        const deferPreviewStart = () => {
          const schedule = () => {
            const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 220));
            idle(setupPreviewObserver, { timeout: 1800 });
          };

          if (document.readyState === 'complete') schedule();
          else window.addEventListener('load', schedule, { once: true });
        };

        const setBuffering = (on) => {
          viewer.classList.toggle('is-buffering', Boolean(on));
        };

        const clearStallWatch = () => {
          if (stallTimer) {
            window.clearTimeout(stallTimer);
            stallTimer = 0;
          }
        };

        const watchForStall = () => {
          clearStallWatch();
          stallTimer = window.setTimeout(() => {
            stallTimer = 0;
            if (!lightbox.classList.contains('is-open')) return;
            if (lightboxVideo.readyState >= 3) return;

            setBuffering(true);
            if (stallRetried) return;
            stallRetried = true;
            lightboxVideo.load();
            if (!reducedMotionQuery?.matches) lightboxVideo.play().catch(() => {});
          }, 1600);
        };

        const setPlaybackIcon = () => {
          const playing = !lightboxVideo.paused && !lightboxVideo.ended;
          playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
          playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        };

        const setMuteIcon = () => {
          const isMuted = lightboxVideo.muted;
          muteButton.classList.toggle('is-muted', isMuted);
          muteButton.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
          muteButton.setAttribute('aria-label', isMuted ? 'Turn sound on' : 'Mute video');
        };

        const hideToast = () => {
          toast?.classList.remove('is-on');
          if (toastTimer) {
            window.clearTimeout(toastTimer);
            toastTimer = 0;
          }
        };

        const showToast = (name) => {
          if (!toast) return;
          if (toastName) toastName.textContent = name || '';
          toast.classList.add('is-on');
          if (toastTimer) window.clearTimeout(toastTimer);
          toastTimer = window.setTimeout(hideToast, 3600);
        };

        const warmedProductImages = new Map();
        const warmedVideos = new Map();
        const variantMapCache = new WeakMap();

        const getQuickCard = () => productSlot.querySelector('[data-wirs-quick-card]');

        const warmProductImage = (card) => {
          if (!card) return;

          const src = card.dataset.productImage || '';
          if (!src || warmedProductImages.has(src)) return;

          const image = new Image();
          image.decoding = 'async';
          image.src = src;
          warmedProductImages.set(src, image);
        };

        const warmReelVideo = (card) => {
          if (!card) return;
          const src = card.dataset.video || '';
          if (!src || warmedVideos.has(src)) return;

          const connection = previewConnection();
          if (connection?.saveData) return;
          if (['slow-2g', '2g'].includes(connection?.effectiveType || '')) return;

          const video = document.createElement('video');
          video.muted = true;
          video.preload = 'metadata';
          video.src = src;
          warmedVideos.set(src, video);
        };

        const warmAdjacentVideos = (index) => {
          if (cards.length < 2) return;
          warmReelVideo(getCard(getWrappedIndex(index - 1)));
          warmReelVideo(getCard(getWrappedIndex(index + 1)));
        };

        const renderProductCard = (index) => {
          productSlot.textContent = '';

          const template = getTemplate('product', index);
          const fragment = cloneTemplate(template);
          if (!fragment) return;

          const card = getCard(index);
          const image = fragment.querySelector('[data-wirs-card-image]');

          if (image) {
            const src = image.dataset.src || card?.dataset.productImage || '';
            const fallback = card?.dataset.poster || '';

            if (src) {
              image.fetchPriority = 'high';
              image.src = src;

              image.addEventListener('error', () => {
                if (fallback && image.src !== fallback) image.src = fallback;
              }, { once: true });
            } else if (fallback) {
              image.src = fallback;
            }
          }

          productSlot.appendChild(fragment);
        };

        const renderVariantSeed = (index) => {
          variantSlot.textContent = '';
          const template = getTemplate('variant', index);
          const fragment = cloneTemplate(template);
          if (fragment) variantSlot.appendChild(fragment);
        };

        const loadLightboxMedia = (index) => {
          const card = getCard(index);
          if (!card) return;

          const poster = card.dataset.poster || '';
          const video = card.dataset.video || '';

          clearStallWatch();
          stallRetried = false;
          setBuffering(false);

          lightboxVideo.pause();
          lightboxVideo.removeAttribute('src');
          lightboxVideo.load();
          setPlaybackIcon();

          if (poster) lightboxVideo.poster = poster;
          else lightboxVideo.removeAttribute('poster');

          if (!video) return;

          lightboxVideo.preload = 'auto';
          lightboxVideo.muted = !userUnmutedAudio;
          setMuteIcon();
          lightboxVideo.setAttribute('src', video);
          lightboxVideo.load();

          if (reducedMotionQuery?.matches) {
            setPlaybackIcon();
            return;
          }

          const attempt = lightboxVideo.play();
          if (attempt && typeof attempt.then === 'function') {
            attempt
              .catch(() => {
                if (!lightboxVideo.muted) {
                  lightboxVideo.muted = true;
                  userUnmutedAudio = false;
                  setMuteIcon();
                  lightboxVideo.play().catch(() => {});
                }
              })
              .finally(() => {
                setPlaybackIcon();
                watchForStall();
              });
          } else {
            setPlaybackIcon();
            watchForStall();
          }
        };

        const closeVariantSheet = () => {
          if (variantController) {
            variantController.abort();
            variantController = null;
          }

          variantSheet.classList.remove('is-open');
          variantSheet.setAttribute('aria-hidden', 'true');
          variantBackdrop.hidden = true;
        };

        const openVariantSheet = () => {
          renderVariantSeed(currentIndex);

          const selector = variantSlot.querySelector('[data-wirs-variant-selector]');
          if (!selector) return;

          variantBackdrop.hidden = false;
          variantSheet.classList.add('is-open');
          variantSheet.setAttribute('aria-hidden', 'false');

          window.requestAnimationFrame(() => {
            selector.querySelector('[data-wirs-option][aria-pressed="true"], [data-wirs-option], [data-wirs-variant-confirm]')?.focus();
          });
        };

        const loadReel = (requestedIndex) => {
          if (!cards.length) return;

          let index = requestedIndex;
          if (index < 0) index = cards.length - 1;
          if (index >= cards.length) index = 0;
          currentIndex = index;

          closeVariantSheet();
          renderProductCard(index);
          if (counterCurrent) counterCurrent.textContent = String(index + 1);
          loadLightboxMedia(index);
          warmAdjacentVideos(index);
        };

        const openLightbox = (index) => {
          lastFocus = document.activeElement;
          previousHtmlOverflow = document.documentElement.style.overflow;
          previousBodyOverflow = document.body.style.overflow;

          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';

          warmProductImage(getCard(index));
          warmReelVideo(getCard(index));
          warmAdjacentVideos(index);
          pauseAllPreviews(true);

          lightbox.classList.add('is-open');
          lightbox.setAttribute('aria-hidden', 'false');

          loadReel(index);
          window.requestAnimationFrame(() => closeButton.focus());
        };

        const closeLightbox = () => {
          if (!lightbox.classList.contains('is-open')) return;

          closeVariantSheet();
          lightbox.classList.remove('is-open');
          lightbox.setAttribute('aria-hidden', 'true');

          document.documentElement.style.overflow = previousHtmlOverflow;
          document.body.style.overflow = previousBodyOverflow;

          clearStallWatch();
          setBuffering(false);
          lightboxVideo.pause();
          setPlaybackIcon();
          lightboxVideo.removeAttribute('src');
          lightboxVideo.load();

          productSlot.textContent = '';
          variantSlot.textContent = '';

          queuePreviewSync();

          if (lastFocus && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
          }
        };

        const setQuickCardImage = (src) => {
          if (!src) return;

          const image = getQuickCard()?.querySelector('[data-wirs-card-image]');
          if (!image) return;

          if (image.src !== src) image.src = src;
        };

        const updateQuickCardFromSelector = (selector) => {
          const card = getQuickCard();
          if (!card || !selector) return;

          const title = selector.dataset.productTitle || '';
          const productLink = selector.dataset.productLink || selector.dataset.productUrl || '#';
          const image = selector.dataset.productImage || '';
          const price = selector.dataset.price || '';
          const compare = selector.dataset.compare || '';
          const discount = selector.dataset.discount || '';
          const variantId = selector.dataset.variantId || '';
          const available = selector.dataset.variantAvailable === 'true';

          card.dataset.productUrl = productLink;

          card.querySelectorAll('[data-wirs-card-title]').forEach((element) => {
            element.textContent = title;
            element.href = productLink;
          });

          card.querySelectorAll('[data-wirs-card-link], [data-wirs-card-view]').forEach((element) => {
            element.href = productLink;
          });

          if (image) setQuickCardImage(image);

          const priceElement = card.querySelector('[data-wirs-card-price]');
          if (priceElement) priceElement.textContent = price;

          const compareElement = card.querySelector('[data-wirs-card-compare]');
          if (compareElement) {
            compareElement.textContent = compare;
            compareElement.hidden = !compare;
          }

          const discountElement = card.querySelector('[data-wirs-card-discount]');
          if (discountElement) {
            discountElement.textContent = discount;
            discountElement.hidden = !discount;
          }

          const addButton = card.querySelector('[data-wirs-product-add]');
          if (addButton) {
            addButton.dataset.variantId = variantId;
            addButton.disabled = !available;
            addButton.textContent = available ? 'Add to cart' : 'Sold out';
          }
        };

        const getVariantMap = (selector) => {
          if (!selector || selector.dataset.localMap !== 'true') return null;
          if (variantMapCache.has(selector)) return variantMapCache.get(selector);

          const script = selector.querySelector('[data-wirs-variant-map]');
          if (!script) return null;

          try {
            const parsed = JSON.parse(script.textContent || '[]');
            const map = Array.isArray(parsed) ? parsed : [];
            variantMapCache.set(selector, map);
            return map;
          } catch (error) {
            console.warn('[WI Reel Shop] Local variant map failed to parse.', error);
            variantMapCache.set(selector, []);
            return [];
          }
        };

        const getOptionGroups = (selector) =>
          Array.from(selector.querySelectorAll('.wi-reel-shop__variant-option[data-option-position]'))
            .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));

        const markOptionSelected = (button) => {
          const group = button.closest('.wi-reel-shop__variant-option');
          if (!group) return;

          group.querySelectorAll('[data-wirs-option]').forEach((chip) => {
            chip.setAttribute('aria-pressed', chip === button ? 'true' : 'false');
          });
        };

        const selectedOptionValues = (selector) =>
          getOptionGroups(selector).map((group) => {
            const selected = group.querySelector('[data-wirs-option][aria-pressed="true"]');
            return selected?.dataset.optionValue || '';
          });

        const selectedOptionIds = (selector) =>
          getOptionGroups(selector)
            .map((group) => {
              const selected = group.querySelector('[data-wirs-option][aria-pressed="true"]');
              return selected?.dataset.optionValueId || '';
            })
            .filter(Boolean);

        const setSelectorStatus = (selector, message = '', type = '') => {
          const status = selector?.querySelector('[data-wirs-variant-status]');
          if (!status) return;

          status.textContent = message;
          status.hidden = !message;

          if (type) status.dataset.state = type;
          else status.removeAttribute('data-state');
        };

        const applyVariantData = (selector, variant) => {
          if (!selector || !variant) return;

          const available = Boolean(variant.available);
          const variantId = String(variant.id || '');
          const image = variant.image || '';

          selector.dataset.variantId = variantId;
          selector.dataset.variantAvailable = String(available);
          selector.dataset.price = variant.price || '';
          selector.dataset.compare = variant.compare || '';
          selector.dataset.discount = variant.discount || '';

          if (image) selector.dataset.productImage = image;

          const confirm = selector.querySelector('[data-wirs-variant-confirm]');
          if (confirm) {
            confirm.dataset.variantId = variantId;
            confirm.disabled = !available;
            confirm.textContent = available
              ? `Add to cart · ${variant.price || ''}`.trim()
              : 'Sold out';
          }

          setSelectorStatus(selector);
          updateQuickCardFromSelector(selector);
        };

        const updateLocalAvailability = (selector, variants) => {
          if (!selector || !variants?.length) return;

          const groups = getOptionGroups(selector);
          const selectedValues = selectedOptionValues(selector);

          groups.forEach((group) => {
            const position = Number.parseInt(group.dataset.optionPosition, 10) - 1;

            group.querySelectorAll('[data-wirs-option]').forEach((chip) => {
              const candidateValues = selectedValues.slice();
              candidateValues[position] = chip.dataset.optionValue || '';

              const isAvailable = variants.some((variant) =>
                variant.available &&
                Array.isArray(variant.options) &&
                variant.options.length === candidateValues.length &&
                variant.options.every((value, index) => value === candidateValues[index])
              );

              if (isAvailable) {
                chip.removeAttribute('data-unavailable');
                chip.disabled = false;
              } else {
                chip.dataset.unavailable = 'true';
                chip.disabled = true;
              }
            });
          });
        };

        const applyDirectVariantFromButton = (selector, button) => {
          const id = button.dataset.directVariantId || '';
          if (!id) return false;

          applyVariantData(selector, {
            id,
            available: button.dataset.directAvailable === 'true',
            price: button.dataset.directPrice || '',
            compare: button.dataset.directCompare || '',
            discount: button.dataset.directDiscount || '',
            image: button.dataset.directImage || ''
          });

          return true;
        };

        const selectLocalVariant = (selector, button) => {
          const variants = getVariantMap(selector);
          if (!variants?.length) return false;

          markOptionSelected(button);

          const selectedValues = selectedOptionValues(selector);
          const variant = variants.find((item) =>
            Array.isArray(item.options) &&
            item.options.length === selectedValues.length &&
            item.options.every((value, index) => value === selectedValues[index])
          );

          if (variant) applyVariantData(selector, variant);
          updateLocalAvailability(selector, variants);

          return true;
        };

        const fetchVariantSelector = async (selector, preferredProductUrl = '') => {
          if (!selector) return;

          const ids = selectedOptionIds(selector);
          if (!ids.length) return;

          const selectedButton = selector.querySelector('[data-wirs-option][aria-pressed="true"][data-product-url]');
          const requestedProductUrl = preferredProductUrl || selectedButton?.dataset.productUrl || selector.dataset.productUrl;
          if (!requestedProductUrl) return;

          const url = new URL(requestedProductUrl, window.location.origin);
          url.searchParams.set('section_id', 'wi-reel-shop-variant-panel');
          url.searchParams.set('option_values', ids.join(','));

          const cacheKey = url.toString();
          const controller = new AbortController();

          if (variantController) variantController.abort();
          variantController = controller;

          let slowStatusTimer = window.setTimeout(() => {
            if (selector.isConnected) {
              setSelectorStatus(selector, 'Checking availability…', 'loading');
            }
          }, 450);

          try {
            let html = variantCache.get(cacheKey);

            if (!html) {
              const response = await fetch(cacheKey, {
                headers: { Accept: 'text/html' },
                signal: controller.signal
              });

              if (!response.ok) {
                throw new Error(`Variant update failed (${response.status})`);
              }

              html = await response.text();
              variantCache.set(cacheKey, html);

              if (variantCache.size > 18) {
                variantCache.delete(variantCache.keys().next().value);
              }
            }

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const nextSelector = doc.querySelector('[data-wirs-variant-selector]');

            if (!nextSelector) {
              throw new Error('Variant selector missing from response.');
            }

            const imported = document.importNode(nextSelector, true);
            selector.replaceWith(imported);

            updateQuickCardFromSelector(imported);
          } catch (error) {
            if (error?.name === 'AbortError') return;

            console.warn('[WI Reel Shop] Variant update failed.', error);

            if (selector.isConnected) {
              setSelectorStatus(selector, 'Could not verify availability. Please try again.', 'error');
            }
          } finally {
            window.clearTimeout(slowStatusTimer);
            slowStatusTimer = 0;

            if (variantController === controller) {
              variantController = null;
            }
          }
        };

        const handleVariantOption = (button) => {
          const selector = button.closest('[data-wirs-variant-selector]');
          if (!selector || button.disabled) return;

          if (button.getAttribute('aria-pressed') === 'true') return;

          const currentProductUrl = selector.dataset.productUrl || '';
          const nextProductUrl = button.dataset.productUrl || currentProductUrl;
          const switchesProduct = Boolean(nextProductUrl && currentProductUrl && nextProductUrl !== currentProductUrl);

          if (!switchesProduct && selectLocalVariant(selector, button)) {
            return;
          }

          markOptionSelected(button);

          const optionCount = Number.parseInt(selector.dataset.optionCount || '0', 10);

          if (!switchesProduct && optionCount === 1 && applyDirectVariantFromButton(selector, button)) {
            return;
          }

          if (!switchesProduct) {
            applyDirectVariantFromButton(selector, button);
          }

          fetchVariantSelector(selector, nextProductUrl);
        };

        const setAddButtonState = (button, state, label = '') => {
          if (!button) return;

          button.classList.toggle('is-loading', state === 'loading');
          button.classList.toggle('is-added', state === 'added');

          if (state === 'loading') {
            button.setAttribute('aria-busy', 'true');
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('aria-busy');
            button.removeAttribute('aria-disabled');
          }

          if (label) button.textContent = label;
        };

        const getCartUi = () =>
          document.querySelector('cart-drawer, cart-notification');

        const getCartSectionIds = (cartUi) => {
          if (!cartUi || typeof cartUi.getSectionsToRender !== 'function') return [];

          return [...new Set(
            cartUi
              .getSectionsToRender()
              .map((item) => item.id || item.section)
              .filter(Boolean)
          )].slice(0, 5);
        };

        const addToCart = async (variantId, button) => {
          if (isAdding || !variantId) return;
          isAdding = true;

          const original = button?.textContent || 'Add to cart';
          const cartUi = getCartUi();
          const cartSections = getCartSectionIds(cartUi);
          const quickCard = getQuickCard();
          const productName = quickCard?.querySelector('[data-wirs-card-title]')?.textContent?.trim() || '';

          if (variantController) {
            variantController.abort();
            variantController = null;
          }

          lightboxVideo.pause();
          setPlaybackIcon();
          setAddButtonState(button, 'loading', 'Adding…');

          const payload = {
            items: [{ id: Number(variantId), quantity: 1 }]
          };

          if (cartSections.length) {
            payload.sections = cartSections;
            payload.sections_url = window.location.pathname;
          }

          try {
            const response = await fetch(cartAddUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              credentials: 'same-origin',
              cache: 'no-store',
              body: JSON.stringify(payload)
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw result;

            setAddButtonState(button, 'added', 'Added ✓');
            closeVariantSheet();
            isAdding = false;

            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('theme:cart:update', {
              bubbles: true,
              detail: { cart: result }
            }));

            if (cartUi && result.sections && typeof cartUi.renderContents === 'function') {
              if (typeof cartUi.setActiveElement === 'function') {
                cartUi.setActiveElement(lastFocus || button);
              }

              result.id = result.id || Number(variantId);
              hideToast();
              closeLightbox();
              cartUi.renderContents(result);
              return;
            }

            showToast(productName);

            if (lightbox.classList.contains('is-open') && lightboxVideo.src && !reducedMotionQuery?.matches) {
              lightboxVideo.play().catch(() => {}).finally(setPlaybackIcon);
            }

            if (button) {
              window.setTimeout(() => {
                if (!button.isConnected) return;
                button.classList.remove('is-added');
                setAddButtonState(button, 'idle', original);
              }, 1300);
            }
          } catch (error) {
            isAdding = false;
            console.warn('[WI Reel Shop] Add to cart failed.', error);

            if (lightbox.classList.contains('is-open') && lightboxVideo.src && !reducedMotionQuery?.matches) {
              lightboxVideo.play().catch(() => {}).finally(setPlaybackIcon);
            }

            if (button) {
              button.classList.remove('is-added');
              setAddButtonState(button, 'idle', error?.description || 'Try again');
              window.setTimeout(() => {
                if (button.isConnected) setAddButtonState(button, 'idle', original);
              }, 1800);
            }
          }
        };

        const handleRootClick = (event) => {
          const card = event.target.closest('[data-wirs-card]');
          if (card && root.contains(card)) {
            const index = Number.parseInt(card.dataset.index, 10) || 0;
            openLightbox(index);
            return;
          }

          if (event.target.closest('[data-wirs-close]')) {
            closeLightbox();
            return;
          }

          if (event.target.closest('[data-wirs-play]')) {
            if (lightboxVideo.paused || lightboxVideo.ended) {
              lightboxVideo.play().catch(() => {}).finally(setPlaybackIcon);
            } else {
              lightboxVideo.pause();
              setPlaybackIcon();
            }
            return;
          }

          if (event.target.closest('[data-wirs-mute]')) {
            lightboxVideo.muted = !lightboxVideo.muted;
            userUnmutedAudio = !lightboxVideo.muted;
            setMuteIcon();
            return;
          }

          if (event.target.closest('[data-wirs-prev]')) {
            loadReel(currentIndex - 1);
            return;
          }

          if (event.target.closest('[data-wirs-next]')) {
            loadReel(currentIndex + 1);
            return;
          }

          const addButton = event.target.closest('[data-wirs-product-add]');
          if (addButton) {
            if (addButton.dataset.requiresOptions === 'true') openVariantSheet();
            else addToCart(addButton.dataset.variantId, addButton);
            return;
          }

          const optionButton = event.target.closest('[data-wirs-option]');
          if (optionButton && variantSlot.contains(optionButton)) {
            handleVariantOption(optionButton);
            return;
          }

          const confirmButton = event.target.closest('[data-wirs-variant-confirm]');
          if (confirmButton && variantSlot.contains(confirmButton)) {
            addToCart(confirmButton.dataset.variantId, confirmButton);
            return;
          }

          if (event.target.closest('[data-wirs-variant-close]')) {
            closeVariantSheet();
          }
        };

        const handleBackdropClick = (event) => {
          if (event.target === lightbox) closeLightbox();
          if (event.target === variantBackdrop) closeVariantSheet();
        };

        const handleKeydown = (event) => {
          if (!lightbox.classList.contains('is-open')) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            if (variantSheet.classList.contains('is-open')) closeVariantSheet();
            else closeLightbox();
            return;
          }

          if (!variantSheet.classList.contains('is-open')) {
            if (event.key === 'ArrowLeft' && prevButton) {
              event.preventDefault();
              loadReel(currentIndex - 1);
              return;
            }
            if (event.key === 'ArrowRight' && nextButton) {
              event.preventDefault();
              loadReel(currentIndex + 1);
              return;
            }
          }

          if (event.key !== 'Tab') return;

          const focusRoot = variantSheet.classList.contains('is-open') ? variantSheet : lightbox;
          const focusable = Array.from(focusRoot.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
            .filter((element) => element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden');

          if (!focusable.length) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        };

        const handlePointerDown = (event) => {
          if (!lightbox.classList.contains('is-open')) return;
          if (variantSheet.classList.contains('is-open')) return;
          if (event.target.closest('button, a, input, select, [data-wirs-quick-card]')) return;

          pointerStartX = event.clientX;
          pointerStartY = event.clientY;
        };

        const handlePointerUp = (event) => {
          if (!lightbox.classList.contains('is-open')) return;
          if (variantSheet.classList.contains('is-open')) return;
          if (event.target.closest('button, a, input, select, [data-wirs-quick-card]')) return;

          const dx = event.clientX - pointerStartX;
          const dy = event.clientY - pointerStartY;
          if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
          loadReel(dx < 0 ? currentIndex + 1 : currentIndex - 1);
        };

        const handleWarmIntent = (event) => {
          const card = event.target.closest?.('[data-wirs-card]');
          if (card && root.contains(card)) {
            warmProductImage(card);
            warmReelVideo(card);
            const index = Number.parseInt(card.dataset.index, 10);
            if (Number.isFinite(index)) warmAdjacentVideos(index);
          }
        };

        const handleVisibility = () => queuePreviewSync();

        const handleReducedMotion = (event) => {
          if (event.matches) {
            teardownPreviews();
            if (lightbox.classList.contains('is-open')) {
              lightboxVideo.pause();
              setPlaybackIcon();
            }
          } else {
            deferPreviewStart();
          }
        };

        const handleDesignModeBlockSelect = (event) => {
          const blockId = event.detail?.blockId;
          if (!blockId) return;

          const selectedCard = root.querySelector(`[data-block-id="${blockId}"]`);
          if (!selectedCard) return;

          selectedCard.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          const index = Number.parseInt(selectedCard.dataset.index, 10);
          if (Number.isFinite(index) && lightbox.classList.contains('is-open')) loadReel(index);
          queuePreviewSync();
        };

        const handleLightboxWaiting = () => setBuffering(true);
        const handleLightboxPlaying = () => {
          clearStallWatch();
          setBuffering(false);
          setPlaybackIcon();
          warmAdjacentVideos(currentIndex);
        };
        const handleLightboxCanPlay = () => {
          clearStallWatch();
          setBuffering(false);
        };
        const handleLightboxError = () => {
          clearStallWatch();
          setBuffering(false);
          setPlaybackIcon();
        };

        root.addEventListener('click', handleRootClick);
        root.addEventListener('pointerover', handleWarmIntent, { passive: true });
        root.addEventListener('pointerdown', handleWarmIntent, { passive: true });
        lightbox.addEventListener('click', handleBackdropClick);
        viewer.addEventListener('pointerdown', handlePointerDown, { passive: true });
        viewer.addEventListener('pointerup', handlePointerUp, { passive: true });
        document.addEventListener('keydown', handleKeydown);
        document.addEventListener('visibilitychange', handleVisibility);

        if (reducedMotionQuery) {
          if (typeof reducedMotionQuery.addEventListener === 'function') reducedMotionQuery.addEventListener('change', handleReducedMotion);
          else reducedMotionQuery.addListener?.(handleReducedMotion);
        }

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:block:select', handleDesignModeBlockSelect);
          document.addEventListener('shopify:block:deselect', queuePreviewSync);
          document.addEventListener('shopify:section:select', queuePreviewSync);
        }

        lightboxVideo.addEventListener('play', setPlaybackIcon);
        lightboxVideo.addEventListener('pause', setPlaybackIcon);
        lightboxVideo.addEventListener('ended', setPlaybackIcon);
        lightboxVideo.addEventListener('waiting', handleLightboxWaiting);
        lightboxVideo.addEventListener('stalled', handleLightboxWaiting);
        lightboxVideo.addEventListener('playing', handleLightboxPlaying);
        lightboxVideo.addEventListener('canplay', handleLightboxCanPlay);
        lightboxVideo.addEventListener('error', handleLightboxError);

        setPlaybackIcon();
        setMuteIcon();
        deferPreviewStart();

        const warmInitialProductImages = () => {
          cards.slice(0, 3).forEach(warmProductImage);
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warmInitialProductImages, { timeout: 1200 });
        } else {
          window.setTimeout(warmInitialProductImages, 300);
        }

        this.__wirsCleanup = () => {
          if (destroyed) return;
          destroyed = true;

          root.removeEventListener('click', handleRootClick);
          root.removeEventListener('pointerover', handleWarmIntent);
          root.removeEventListener('pointerdown', handleWarmIntent);
          lightbox.removeEventListener('click', handleBackdropClick);
          viewer.removeEventListener('pointerdown', handlePointerDown);
          viewer.removeEventListener('pointerup', handlePointerUp);
          document.removeEventListener('keydown', handleKeydown);
          document.removeEventListener('visibilitychange', handleVisibility);

          if (reducedMotionQuery) {
            if (typeof reducedMotionQuery.removeEventListener === 'function') reducedMotionQuery.removeEventListener('change', handleReducedMotion);
            else reducedMotionQuery.removeListener?.(handleReducedMotion);
          }

          if (window.Shopify?.designMode) {
            document.removeEventListener('shopify:block:select', handleDesignModeBlockSelect);
            document.removeEventListener('shopify:block:deselect', queuePreviewSync);
            document.removeEventListener('shopify:section:select', queuePreviewSync);
          }

          lightboxVideo.removeEventListener('play', setPlaybackIcon);
          lightboxVideo.removeEventListener('pause', setPlaybackIcon);
          lightboxVideo.removeEventListener('ended', setPlaybackIcon);
          lightboxVideo.removeEventListener('waiting', handleLightboxWaiting);
          lightboxVideo.removeEventListener('stalled', handleLightboxWaiting);
          lightboxVideo.removeEventListener('playing', handleLightboxPlaying);
          lightboxVideo.removeEventListener('canplay', handleLightboxCanPlay);
          lightboxVideo.removeEventListener('error', handleLightboxError);

          teardownPreviews();
          variantController?.abort();
          variantController = null;
          if (toastTimer) window.clearTimeout(toastTimer);
          clearStallWatch();

          warmedVideos.forEach((video) => {
            video.removeAttribute('src');
            video.load();
          });
          warmedVideos.clear();
          lightboxVideo.pause();

          if (lightbox.classList.contains('is-open')) {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
          }

          delete root.__wirsCleanup;
          root.dataset.wirsInitialized = 'false';
        };
      }

      disconnectedCallback() {
        if (typeof this.__wirsCleanup === 'function') {
          this.__wirsCleanup();
        }
      }
    });
  }
})();